import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewChild,
    afterNextRender,
    input,
    signal,
    Injector,
    AfterContentChecked,
    inject,
} from '@angular/core';
import { UiLinkComponent } from '@big-direkt/ui/link';
import { IconBigBoldGesperrterContent, IconBigMediumWinkelRechts, IconComponent } from '@big-direkt/utils/icons';
import { type BreadcrumbItemModel } from '@big-direkt/utils/shared';
import { translateObjectSignal } from '@jsverse/transloco';

/**
 * To display not expanded breadcrumb correctly select 'Is Not Expanded' in the left hand navigation.
 *
 * Then click at 'Change the size of the viewport' icon in the upper icon bar and select 'Small mobile' option.
 */
@Component({
    selector: 'big-ui-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '(window:resize)': 'this.updateOverflow()',
    },
    imports: [IconComponent, UiLinkComponent],
})
export class BreadcrumbComponent implements AfterContentChecked {
    private readonly maxElementsToOverflow = 3;
    private readonly injector = inject(Injector);

    public readonly arrowIcon = IconBigMediumWinkelRechts;
    public readonly myBigIcon = IconBigBoldGesperrterContent;

    public readonly entries = input<BreadcrumbItemModel[]>([]);

    public readonly breadcrumbIsExpanded = signal<boolean>(false);
    public readonly hasOverflow = signal<boolean>(false);
    public readonly showAllTranslation = translateObjectSignal('uiBreadcrumb.showAll');

    @ViewChild('breadcrumbElement') private readonly breadcrumbElement?: ElementRef<Element>;

    public ngAfterContentChecked(): void {
        if (this.entries().length < this.maxElementsToOverflow || this.hasOverflow()) {
            return;
        }

        afterNextRender(
            () => {
                this.updateOverflow();
            },
            { injector: this.injector },
        );
    }

    public updateOverflow(): void {
        const children = this.breadcrumbElement?.nativeElement.children;
        const lastChild = children?.[children.length - 1];
        const newOverflow = !!lastChild && lastChild.clientWidth < lastChild.scrollWidth;

        if (newOverflow === this.hasOverflow()) {
            return;
        }

        this.hasOverflow.set(newOverflow);
    }

    public expandBreadcrumb(): void {
        this.breadcrumbIsExpanded.set(true);
    }
}
